import { logCorrelationId } from '@brighthub/structured-logging';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  signal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ConfirmationService } from 'primeng/api';
import { Button } from 'primeng/button';
import { Checkbox } from 'primeng/checkbox';
import { ConfirmDialog } from 'primeng/confirmdialog';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { Textarea } from 'primeng/textarea';
import { CarePlanAnalytics } from '../../analytics/care-plan-analytics';
import { CARE_PLAN_MAX_ACTIVE_GOALS } from '../../care-plan.model';
import {
  CarePlanStore,
  GoalSaveError,
  LiveGoalWrite,
} from '../../care-plan.store';

export interface ManageCaseGoalsDialogData {
  store: CarePlanStore;
  caseId: string;
}

/**
 * Case-scoped goals modal: link/unlink active client goals to this case, and
 * create a new goal when under the 3-active cap so the FA does not have to
 * leave the case for the goals-bar dialog. Staged locally; Save commits,
 * Cancel (via `tryClose`) discards with the D18 unsaved-changes guard.
 */
@Component({
  selector: 'bn-manage-case-goals-dialog',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [FormsModule, Button, Checkbox, ConfirmDialog, Textarea],
  providers: [ConfirmationService],
  templateUrl: './manage-case-goals-dialog.component.html',
  styleUrl: './manage-case-goals-dialog.component.scss',
})
export class ManageCaseGoalsDialogComponent {
  private readonly _goalLogKey = logCorrelationId();
  private readonly _ref = inject(DynamicDialogRef);
  private readonly _config = inject(DynamicDialogConfig);
  private readonly _confirmation = inject(ConfirmationService);
  private readonly _analytics = inject(CarePlanAnalytics);
  private readonly _data = this._config.data as ManageCaseGoalsDialogData;

  protected readonly store = this._data.store;
  protected readonly caseId = this._data.caseId;
  constructor() {
    effect(() => {
      if (!this.store.goalsEnabled()) this._ref.close();
    });
  }
  protected readonly maxActiveGoals = CARE_PLAN_MAX_ACTIVE_GOALS;

  private readonly _initialGoalIds: string[] = [
    ...(
      this.store.cases().find((c) => c.id === this.caseId)?.goalIds ?? []
    ).filter((id) => this.store.activeGoals().some((goal) => goal.id === id)),
  ];

  readonly selectedGoalIds = signal<string[]>([...this._initialGoalIds]);
  readonly draftGoals = signal<string[]>([]);
  readonly saving = signal(false);
  readonly saveError = signal<string | null>(null);
  readonly liveSaveStarted = signal(false);
  readonly ambiguousCreate = signal(false);
  private remainingWrites: LiveGoalWrite[] | null = null;
  private readonly openingGuid = this.store.sessionGuid();

  protected readonly atCap = computed(
    () =>
      this.store.activeGoals().length + this.draftGoals().length >=
      this.maxActiveGoals,
  );

  protected readonly dirty = computed(() => {
    if (this.draftGoals().some((text) => text.trim().length > 0)) return true;
    const selected = this.selectedGoalIds();
    if (selected.length !== this._initialGoalIds.length) return true;
    const initial = new Set(this._initialGoalIds);
    return selected.some((id) => !initial.has(id));
  });

  toggleGoal(goalId: string, checked: boolean): void {
    this.selectedGoalIds.update((ids) =>
      checked
        ? [...new Set([...ids, goalId])]
        : ids.filter((id) => id !== goalId),
    );
  }

  addDraftRow(): void {
    if (this.atCap()) {
      this._analytics.goalCapBlocked(this.openingGuid);
      return;
    }
    this.draftGoals.update((rows) => [...rows, '']);
  }

  setDraftText(index: number, text: string): void {
    this.draftGoals.update((rows) =>
      rows.map((r, i) => (i === index ? text : r)),
    );
  }

  removeDraftRow(index: number): void {
    this.draftGoals.update((rows) => rows.filter((_, i) => i !== index));
  }

  save(): void {
    if (!this.store.goalsEnabled() || this.saving() || this.ambiguousCreate())
      return;
    if (this.store.isLive()) {
      void this.saveLive();
      return;
    }
    const selected = new Set(this.selectedGoalIds());
    const initial = new Set(this._initialGoalIds);

    for (const goalId of initial) {
      if (!selected.has(goalId)) {
        this.store.unlinkGoal(this.caseId, goalId);
        this._analytics.caseGoalUnlinked(this.caseId, goalId, this.openingGuid);
      }
    }
    for (const goalId of selected) {
      if (!initial.has(goalId)) {
        this.store.linkGoal(this.caseId, goalId);
        this._analytics.caseGoalLinked(this.caseId, goalId, this.openingGuid);
      }
    }

    for (const text of this.draftGoals()) {
      const trimmed = text.trim();
      if (trimmed.length === 0 || trimmed.length > 200) continue;
      const created = this.store.addGoal(trimmed);
      if (created) {
        this.store.linkGoal(this.caseId, created.id);
        this._analytics.goalCreated(
          created.id,
          this.store.activeGoals().length,
          this.openingGuid,
        );
        this._analytics.caseGoalLinked(
          this.caseId,
          created.id,
          this.openingGuid,
        );
      } else {
        this._analytics.goalCapBlocked(this.openingGuid);
      }
    }

    this._ref.close({ saved: true });
  }

  private async saveLive(): Promise<void> {
    if (this.openingGuid !== this.store.sessionGuid()) return;
    if (!this.remainingWrites) {
      const selected = new Set(this.selectedGoalIds());
      const initial = new Set(this._initialGoalIds);
      this.remainingWrites = [
        ...[...initial]
          .filter((id) => !selected.has(id))
          .map((goalId) => ({
            kind: 'unlink' as const,
            goalId,
            caseId: this.caseId,
          })),
        ...[...selected]
          .filter((id) => !initial.has(id))
          .map((goalId) => ({
            kind: 'link' as const,
            goalId,
            caseId: this.caseId,
          })),
        ...this.draftGoals()
          .filter((text) => text.trim())
          .map((description) => ({
            kind: 'create' as const,
            description,
            caseId: this.caseId,
          })),
      ];
    }
    this.saving.set(true);
    this.saveError.set(null);
    this.liveSaveStarted.set(true);
    try {
      await this.store.saveLiveGoals(
        [...this.remainingWrites],
        (change, id) => {
          this.remainingWrites?.shift();
          if (change.kind === 'create' && id) {
            this._analytics.caseGoalLinked(this.caseId, id, this.openingGuid);
          } else if (change.kind === 'link')
            this._analytics.caseGoalLinked(
              this.caseId,
              change.goalId,
              this.openingGuid,
            );
          else if (change.kind === 'unlink')
            this._analytics.caseGoalUnlinked(
              this.caseId,
              change.goalId,
              this.openingGuid,
            );
        },
        true,
        this._goalLogKey,
      );
      this._ref.close({ saved: true });
    } catch (error) {
      this.ambiguousCreate.set(
        error instanceof GoalSaveError && error.ambiguousCreate,
      );
      this.saveError.set(
        (error instanceof Error ? error.message : 'Goals could not be saved.') +
          ' Earlier successful changes are retained.',
      );
    } finally {
      this.saving.set(false);
    }
  }

  tryClose(): void {
    if (this.saving()) return;
    if (!this.dirty()) {
      this._ref.close();
      return;
    }
    this._confirmation.confirm({
      message:
        'You have unsaved changes — are you sure you want to discard them?',
      header: 'Unsaved changes',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Discard',
      rejectLabel: 'Stay',
      acceptButtonStyleClass: 'p-button-danger',
      rejectButtonStyleClass: 'p-button-text',
      accept: () => this._ref.close(),
    });
  }
}
